import { SiteSelector } from "@/components/SiteSelector/SiteSelector";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { useAuth } from "react-oidc-context";
import {
  setSelectedSites,
  useSelectedSites,
} from "@medoma/shared-employee/selectedSites";
import { useQuery } from "@tanstack/react-query";
import { sitesQueryOptions } from "@/api/Patients";
import { useEffect } from "react";
import { type ISite } from "@medoma/models/sites";
import { useLingui } from "@lingui/react/macro";

const AutoSelectSite = ({ site }: { site: ISite }) => {
  const { t } = useLingui();
  const { user } = useAuth();
  if (!user) {
    throw new Error("Should have user when auto selecting site");
  }
  useEffect(() => {
    setSelectedSites([site.id], user.profile.sub);
  }, [site.id, user.profile.sub]);
  return <Loading padding={24} message={t`Väljer den tillgängliga enheten`} />;
};

export const RequireSiteSelection = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { t } = useLingui();
  const { user } = useAuth();
  if (!user) {
    throw new Error("Should have user when requiring site selection");
  }

  const selectedSites = useSelectedSites(user.profile.sub);
  const { data: sites, isPending, isError } = useQuery(sitesQueryOptions);

  const canStillAccessSelectedSites =
    sites &&
    selectedSites?.every((siteId) => sites.some((site) => site.id === siteId));

  if (canStillAccessSelectedSites === true) {
    return children;
  }
  // Without the sites there is no telling whether the user has lost access to
  // the stored selection, so it is used as is.
  if (selectedSites && isError) {
    return children;
  }
  if (isPending) {
    return <Loading padding={24} message={t`Hämtar tillgängliga enheter`} />;
  }
  if (isError) {
    return (
      <ErrorMessage
        padding={24}
        message={t`Kunde inte hämta tillgängliga enheter. Kontakta support.`}
      />
    );
  }

  if (sites.length === 0) {
    return (
      <ErrorMessage
        padding={24}
        message={t`Du har inga tillgängliga enheter. Minst en tillgänglig enhet krävs för att kunna fortsätta. Kontakta support.`}
      />
    );
  }
  // Single site available - auto select
  if (sites.length === 1) {
    return <AutoSelectSite site={sites[0]!} />;
  }

  return <SiteSelector sites={sites} userId={user.profile.sub} />;
};
